import styled from "styled-components";

/* Two-column hero; copy colors for readability on burgundy #4a1a2b */
const heroTeal = "#4e878c";
const heroTealLight = "rgba(78, 135, 140, 0.15)";

export const HeroSection = styled.section`
  --hero-teal: ${heroTeal};
  --hero-teal-light: ${heroTealLight};

  min-height: 100vh;
  width: 100%;
  padding: clamp(80px, 12vh, 140px) clamp(24px, 8vw, 80px) 40px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr minmax(280px, 0.9fr);
  gap: clamp(32px, 5vw, 64px);
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;

  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.8s ease-out,
    visibility 0.8s ease-out;

  &.is-visible {
    opacity: 1;
    visibility: visible;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding-top: clamp(100px, 18vh, 140px);
  }
  @media (max-width: 480px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

export const HeroContent = styled.div`
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

export const Greeting = styled.h1`
  color: var(--teal-90);
  font-size: clamp(22px, 2.5vw, 28px);
  font-weight: 400;
  margin: 0 0 6px 0;
  letter-spacing: 0.02em;
`;

export const Name = styled.h2`
  color: var(--rose);
  font-size: clamp(44px, 7vw, 80px);
  font-weight: 700;
  margin: 0 0 20px 0;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

export const Subtitle = styled.p`
  color: var(--rose-90);
  font-size: clamp(20px, 2.2vw, 26px);
  font-weight: 400;
  line-height: 1.5;
  margin: 0 0 28px 0;
  max-width: 480px;

  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

export const Description = styled.p`
  color: var(--rose-70);
  font-size: clamp(17px, 1.8vw, 20px);
  line-height: 1.65;
  margin: 0;
  max-width: 480px;

  strong {
    color: var(--rose-90);
    font-weight: 600;
  }

  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

/* Right column: diagonal stripe pattern + portrait */
export const PortraitColumn = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;

  @media (max-width: 900px) {
    order: -1;
    min-height: 260px;
  }
`;

export const StripePattern = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-35deg);
  width: 140%;
  height: 140%;
  max-width: 480px;
  max-height: 480px;
  background: repeating-linear-gradient(
    90deg,
    transparent,
    transparent 12px,
    var(--hero-teal-light) 12px,
    var(--hero-teal-light) 14px
  );
  border-radius: 24px;
  pointer-events: none;
  z-index: 0;
`;

export const PortraitFrame = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 320px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(30, 41, 59, 0.12);

  img {
    display: block;
    width: 100%;
    height: auto;
    vertical-align: middle;
    /* Blend with burgundy background: desaturate, warm tint, slight darkening */
    filter: saturate(0.55) sepia(0.12) brightness(0.92) contrast(1.05);
  }

  @media (max-width: 900px) {
    max-width: 260px;
  }
`;

export const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--hero-teal);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: ${(props) => (props.$fadeOut ? 0 : 1)};
  pointer-events: ${(props) => (props.$fadeOut ? "none" : "auto")};
  transition: opacity 0.4s ease-out;

  &::after {
    content: "";
    width: 1px;
    height: 40px;
    background: linear-gradient(
      to bottom,
      var(--hero-teal) 0%,
      transparent 100%
    );
    border-radius: 1px;
  }

  @media (max-width: 900px) {
    bottom: 16px;
  }
`;
